import React, { createContext, useState, useContext, useCallback } from 'react';

const NotificationContext = createContext(null);

export const useNotification = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const [notification, setNotification] = useState(null);

  // Centralizes user feedback so pages can show success/error states without alert().
  const showNotification = useCallback((message, type = 'success') => {
    setNotification({ message, type });
    // Auto dismiss after 3 seconds
    const timer = setTimeout(() => {
      setNotification(null);
    }, 3500);

    return () => clearTimeout(timer);
  }, []);

  const closeNotification = () => setNotification(null);

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      {children}
      
      {/* Toast Render */}
      {notification && (
        <div className="fixed top-4 right-4 z-[9999] max-w-sm w-full bg-white rounded-xl shadow-xl border border-slate-100 overflow-hidden transform translate-y-0 transition-all duration-300 ease-out animate-slide-in">
          <div className="p-4 flex items-start gap-3">
            {/* Status Icon */}
            <div className="flex-shrink-0">
              {notification.type === 'success' ? (
                <div className="h-8 w-8 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600 border border-emerald-100">
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              ) : (
                <div className="h-8 w-8 rounded-full bg-rose-50 flex items-center justify-center text-rose-600 border border-rose-100">
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              )}
            </div>

            {/* Message Content */}
            <div className="flex-grow pt-0.5">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-0.5">
                {notification.type === 'success' ? 'Succès' : 'Information / Erreur'}
              </h4>
              <p className="text-sm font-semibold text-slate-800 leading-snug">
                {notification.message}
              </p>
            </div>

            {/* Close Button */}
            <button 
              onClick={closeNotification}
              className="text-slate-400 hover:text-slate-600 p-0.5 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {/* Progress Bar Animation */}
          <div className="h-1 bg-slate-100 w-full relative">
            <div 
              className={`h-full absolute left-0 top-0 transition-all duration-3500 ease-linear animate-shrink ${
                notification.type === 'success' ? 'bg-emerald-500' : 'bg-rose-500'
              }`}
              style={{ width: '100%' }}
            />
          </div>
        </div>
      )}
    </NotificationContext.Provider>
  );
};
